import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Mobile vertical DRAG pager + auto-hiding dot rail for the dashboard photo
 * grid. The page tracks the finger 1:1 while dragging (not a flick-then-jump):
 * the live pane and the incoming neighbour ride together as one surface; on
 * release the pair snaps to the next page (enough distance or a fast flick)
 * or settles back.
 *
 * Wiring (see ProofGrid):
 *  - spread `touchHandlers` on the section's root;
 *  - put `liveRef` on the live page pane;
 *  - while `drag` is set, mount the page `drag.neighbor` in a sibling pane
 *    with `ghostRef` and an initial `translateY(drag.dir * size)` transform —
 *    the hook then drives both transforms imperatively (no per-move renders);
 *  - the hook calls `onBeforeCommit` right before `setPage` so the caller can
 *    suppress its own page-change slide animation (the drag already animated);
 *  - `onDragStart` fires when a drag engages (cancel any in-flight slide).
 *
 * Pull-to-refresh: a downward drag on the FIRST page never engages, leaving
 * the global pull-to-refresh to fire; on later pages the caller sets
 * `data-owns-pull` (via `ownsPull`) so pull-to-refresh stands down and the
 * downward drag pages back instead.
 */
export function useSwipePager({
  page,
  totalPages,
  setPage,
  enabled,
  size,
  onDragStart,
  onBeforeCommit,
}: {
  page: number;
  totalPages: number;
  setPage: (p: number) => void;
  enabled: boolean;
  /** Extent of one page along the drag axis, px (the grid's height). */
  size: number;
  onDragStart?: () => void;
  onBeforeCommit?: () => void;
}) {
  const [drag, setDrag] = useState<{ neighbor: number; dir: 1 | -1 } | null>(null);
  const liveRef = useRef<HTMLDivElement | null>(null);
  const ghostRef = useRef<HTMLDivElement | null>(null);
  const gest = useRef<{
    x: number;
    y: number;
    mode: 'pending' | 'drag' | 'off';
    dir: 1 | -1;
    target: number;
    off: number;
    lastOff: number;
    lastT: number;
    v: number;
  } | null>(null);
  const settleTimer = useRef<number | null>(null);
  // Latest values for the settle timeout (fires across renders).
  const latest = useRef({ setPage, onBeforeCommit });
  latest.current = { setPage, onBeforeCommit };

  const [dotsVisible, setDotsVisible] = useState(false);
  const dotsTimer = useRef<number | null>(null);
  const revealDots = () => {
    if (!enabled || totalPages <= 1) return;
    setDotsVisible(true);
    if (dotsTimer.current) window.clearTimeout(dotsTimer.current);
    dotsTimer.current = window.setTimeout(() => setDotsVisible(false), 2500);
  };
  useEffect(
    () => () => {
      if (dotsTimer.current) window.clearTimeout(dotsTimer.current);
      if (settleTimer.current) window.clearTimeout(settleTimer.current);
    },
    [],
  );

  // Clear the imperative transforms only AFTER the drag-end render commits, so
  // the new/kept page content and the reset position land in the same frame.
  useLayoutEffect(() => {
    if (drag) return;
    const el = liveRef.current;
    if (el) {
      el.style.transform = '';
      el.style.transition = '';
    }
  }, [drag]);

  const paint = (off: number, transition: string) => {
    const g = gest.current;
    const live = liveRef.current;
    const ghost = ghostRef.current;
    const dir = g?.dir ?? 1;
    if (live) {
      live.style.transition = transition;
      live.style.transform = `translateY(${off}px)`;
    }
    if (ghost) {
      ghost.style.transition = transition;
      ghost.style.transform = `translateY(${off + dir * size}px)`;
    }
  };

  const settle = (commit: boolean) => {
    const g = gest.current;
    if (!g) return;
    const ease = 'transform 240ms cubic-bezier(0.22, 1, 0.36, 1)';
    paint(commit ? -g.dir * size : 0, ease);
    const target = g.target;
    if (settleTimer.current) window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => {
      settleTimer.current = null;
      if (commit) {
        latest.current.onBeforeCommit?.();
        latest.current.setPage(target);
      }
      setDrag(null);
    }, 250);
    gest.current = null;
  };

  const onTouchStart = (e: React.TouchEvent) => {
    if (!enabled) return;
    // Ignore multi-touch (pinch-zoom etc.) — only a single finger drags.
    if (e.touches.length > 1) {
      gest.current = null;
      return;
    }
    if (drag || settleTimer.current) return; // still settling the previous one
    const t = e.touches[0];
    gest.current = t
      ? { x: t.clientX, y: t.clientY, mode: 'pending', dir: 1, target: page, off: 0, lastOff: 0, lastT: performance.now(), v: 0 }
      : null;
    revealDots();
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const g = gest.current;
    if (!enabled || !g || g.mode === 'off') return;
    if (e.touches.length > 1) {
      // A pinch started mid-gesture: abandon the drag where it is.
      if (g.mode === 'drag') settle(false);
      else gest.current = null;
      return;
    }
    const t = e.touches[0];
    if (!t) return;
    const dx = t.clientX - g.x;
    const dy = t.clientY - g.y;
    if (g.mode === 'pending') {
      if (Math.abs(dx) > 14 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        g.mode = 'off'; // clearly horizontal → the tab carousel's gesture
        return;
      }
      if (!(Math.abs(dy) > 14 && Math.abs(dy) > Math.abs(dx) * 1.5)) return;
      const dir: 1 | -1 = dy < 0 ? 1 : -1; // drag up → next page
      const target = page + dir;
      if (size <= 0 || target < 0 || target >= totalPages) {
        // No neighbour that way (incl. swipe-down on the first page, which
        // stays a no-op so the global pull-to-refresh can fire).
        g.mode = 'off';
        return;
      }
      g.mode = 'drag';
      g.dir = dir;
      g.target = target;
      onDragStart?.();
      setDrag({ neighbor: target, dir });
      revealDots();
    }
    // Own the gesture — the surrounding tab carousel must not also engage.
    e.stopPropagation();
    // Progress only toward the engaged neighbour; the rest-position is a wall.
    const off = g.dir === 1 ? Math.max(-size, Math.min(0, dy)) : Math.min(size, Math.max(0, dy));
    const now = performance.now();
    if (now > g.lastT) g.v = (off - g.lastOff) / (now - g.lastT);
    g.lastOff = off;
    g.lastT = now;
    g.off = off;
    paint(off, 'none');
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const g = gest.current;
    if (!enabled || !g) return;
    if (g.mode !== 'drag') {
      gest.current = null;
      return;
    }
    e.stopPropagation();
    if (e.touches.length > 0) {
      settle(false); // other fingers still down → was a pinch
      return;
    }
    const moved = g.off * -g.dir; // px of progress toward the neighbour
    const vel = g.v * -g.dir; // px/ms toward the neighbour
    settle(moved > size * 0.28 || (vel > 0.35 && moved > 24));
  };

  const onTouchCancel = () => {
    const g = gest.current;
    if (g?.mode === 'drag') settle(false);
    else gest.current = null;
  };

  return {
    dotsVisible,
    ownsPull: enabled && page > 0,
    touchHandlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel },
    /** While set: mount page `neighbor` in a ghost pane offset by `dir * size`. */
    drag,
    liveRef,
    ghostRef,
  };
}
